import paper from 'paper';
import {FILL_COLOR, IS_PROPORTIONAL, VECTOR_STROKE_COLOR} from '@/constants/editor';

export const createRoundedPolygonPath = (points, radius) => {
  const path = new paper.Path();
  const len = points.length;

  for (let i = 0; i < len; i++) {
    const prev = points[(i - 1 + len) % len];
    const curr = points[i];
    const next = points[(i + 1) % len];

    const toPrev = prev.subtract(curr);
    const toNext = next.subtract(curr);

    const edgeLenPrev = toPrev.length;
    const edgeLenNext = toNext.length;

    let diffAngle = Math.abs(toNext.angle - toPrev.angle);
    if (diffAngle > 180) diffAngle = 360 - diffAngle;

    const halfAngleRad = ((180 - diffAngle) * Math.PI) / 360;
    const maxCut = radius * Math.tan(halfAngleRad);

    const cut = Math.max(
      0,
      Math.min(maxCut, edgeLenPrev / 2, edgeLenNext / 2)
    );

    const p1 = curr.add(toPrev.normalize(cut));
    const p2 = curr.add(toNext.normalize(cut));

    if (i === 0) {
      path.moveTo(p1);
    } else {
      path.lineTo(p1);
    }

    path.quadraticCurveTo(curr, p2);
  }

  path.closePath();

  return path;
};

const createRhombus = (boundsRect) => {
  const size = Math.min(boundsRect.width, boundsRect.height);
  const {center} = boundsRect;
  const half = size / 2;
  const points = [
    new paper.Point(center.x, center.y - half),
    new paper.Point(center.x + half, center.y),
    new paper.Point(center.x, center.y + half),
    new paper.Point(center.x - half, center.y)
  ];
  const path = new paper.Path({
    segments: points,
    closed: true
  });
  path.data = {points};
  return path;
};

const createStar = (boundsRect, pointsCount = 5, inset = 0.5) => {
  const center = boundsRect.center;
  const radius = Math.min(boundsRect.width, boundsRect.height) / 2;
  const points = [];

  for (let i = 0; i < pointsCount * 2; i++) {
    const angle = (i * Math.PI) / pointsCount - Math.PI / 2;
    const r = i % 2 === 0 ? radius : radius * inset;
    points.push(new paper.Point(
      center.x + r * Math.cos(angle),
      center.y + r * Math.sin(angle)
    ));
  }

  const path = new paper.Path({
    segments: points,
    closed: true
  });
  path.data = {points};
  return path;
};


const createArrow = (boundsRect) => {
  const {left, right, top, bottom, center} = boundsRect;
  const path = new paper.Path();

  const start = boundsRect.data?.start || boundsRect.topLeft;
  const end = boundsRect.data?.end || boundsRect.bottomRight;

  const vector = end.subtract(start);
  const length = vector.length;

  if (length < 1) return new paper.Path();

  const headLength = Math.min(length * 0.3, 20);
  const headAngle = 30;

  path.moveTo(start);
  path.lineTo(end);

  const arrowVector = vector.normalize(headLength);

  const leftWing = arrowVector.rotate(180 - headAngle);
  const rightWing = arrowVector.rotate(180 + headAngle);
  const head = new paper.Path({
    segments: [
      end.add(leftWing),
      end,
      end.add(rightWing)
    ],
    closed: true,
    fillColor: path.strokeColor || VECTOR_STROKE_COLOR,
    strokeColor: path.strokeColor || VECTOR_STROKE_COLOR,
    strokeWidth: path.strokeWidth || 1,
    strokeScaling: false
  });

  const group = new paper.Group([path, head]);
  group.data = {type: 'arrow', start, end};

  return group;
};

const createEquilateralTriangle = (boundsRect) => {
  const size = Math.min(boundsRect.width, boundsRect.height);
  const centerX = boundsRect.center.x;

  const height = size * Math.sqrt(3) / 2;

  const bottom = boundsRect.bottom;
  const top = bottom - height;
  const left = centerX - size / 2;
  const right = centerX + size / 2;

  const points = [
    new paper.Point(centerX, top),
    new paper.Point(right, bottom),
    new paper.Point(left, bottom),
  ];

  const path = new paper.Path({
    segments: points,
    closed: true,
  });
  path.data = {points};
  return path;
};

export const buildShapePath = (type, boundsRect) => {
  switch (type) {
    case 'rect':
    case 'square': {
      const path = new paper.Path.Rectangle({
        rectangle: boundsRect,
        data: {type: 'rect'}
      });
      path.data = {...path.data, points: path.segments.map(s => s.point.clone())};
      return path;
    }

    case 'ellipse':
    case 'circle':
      return new paper.Path.Ellipse({
        rectangle: boundsRect,
        data: {type: type}
      });

    case 'triangle': {
      const path = createEquilateralTriangle(boundsRect);
      path.data = {...path.data, type: 'triangle'};
      return path;
    }

    case 'rounded-triangle': {
      const poly = createEquilateralTriangle(boundsRect);
      const points = poly.data.points;
      poly.remove();
      
      const size = Math.min(boundsRect.width, boundsRect.height);
      const cornerRadius = size * 0.1;

      const path = createRoundedPolygonPath(
        points,
        cornerRadius
      );
      path.data = {...path.data, points, radius: cornerRadius, type: 'rounded-triangle'};
      return path;
    }

    case 'hexagon': {
      const size = Math.min(boundsRect.width, boundsRect.height);
      const path = new paper.Path.RegularPolygon({
        center: boundsRect.center,
        sides: 6,
        radius: size / 2,
      });
      path.data = {points: path.segments.map(s => s.point.clone()), type: 'hexagon'};
      path.rotate(30);
      path.data.points = path.segments.map(s => s.point.clone());
      return path;
    }

    case 'octagon': {
      const size = Math.min(boundsRect.width, boundsRect.height);
      const path = new paper.Path.RegularPolygon({
        center: boundsRect.center,
        sides: 8,
        radius: size / 2,
      });
      path.data = {points: path.segments.map(s => s.point.clone()), type: 'octagon'};
      return path;
    }

    case 'star': {
      const path = createStar(boundsRect);
      path.data = {...path.data, type: 'star'};
      return path;
    }

    case 'line': {
      const path = new paper.Path();
      const start = boundsRect.data?.start || boundsRect.topLeft;
      const end = boundsRect.data?.end || boundsRect.bottomRight;
      path.moveTo(start);
      path.lineTo(end);
      return path;
    }

    case 'arrow': {
      const group = createArrow(boundsRect);
      if (boundsRect.data) {
        group.data = {...group.data, ...boundsRect.data};
      }
      return group;
    }

    case 'rhombus': {
      const path = createRhombus(boundsRect);
      path.data = {...path.data, type: 'rhombus'};
      return path;
    }

    case 'polygon-n': {
      const path = new paper.Path.RegularPolygon({
        center: boundsRect.center,
        sides: 5,
        radius: Math.min(boundsRect.width, boundsRect.height) / 2
      });
      path.data = {points: path.segments.map(s => s.point.clone()), type: 'polygon-n'};
      return path;
    }

    default:
      return new paper.Path.Rectangle({
        rectangle: boundsRect,
        data: {type: 'rect'}
      });
  }
};

export const computeDragBounds = (type, start, current) => {
  if (type === 'line' || type === 'arrow') {
    const rect = new paper.Rectangle(start, current);
    rect.data = {start, end: current};
    return rect;
  }

  const dx = current.x - start.x;
  const dy = current.y - start.y;

  const signX = dx < 0 ? -1 : 1;
  const signY = dy < 0 ? -1 : 1;

  let width = Math.abs(dx);
  let height = Math.abs(dy);

  if (IS_PROPORTIONAL.includes(type)) {
    const size = Math.max(width, height);
    width = size;
    height = size;
  }

  const end = new paper.Point(
    start.x + signX * width,
    start.y + signY * height
  );

  return new paper.Rectangle(start, end);
};

export const createShapeFromDrag = (type, start, current) => {
  const boundsRect = computeDragBounds(
    type,
    start,
    current
  );

  const path = buildShapePath(
    type,
    boundsRect
  );

  path.strokeColor = VECTOR_STROKE_COLOR;

  path.strokeWidth = 1;

  path.strokeScaling = false;

  if (type === 'line' || type === 'arrow') {
    path.fillColor = null;
    if (type === 'arrow' && path.children) {
      path.children.forEach(child => {
        child.strokeColor = VECTOR_STROKE_COLOR;
        if (child.closed) {
          child.fillColor = VECTOR_STROKE_COLOR;
        } else {
          child.fillColor = null;
        }
      });
    }
  } else {
    path.fillColor = FILL_COLOR;
  }

  path.data = {...path.data, type};

  return path;
};